import { useRef } from 'react';
import { Link } from 'react-router-dom';
import type { VideoItem } from '@/mocks/videos';
import { formatViews } from '@/lib/videos';

interface PopularRailProps {
  items: VideoItem[];
}

export default function PopularRail({ items }: PopularRailProps) {
  const railRef = useRef<HTMLDivElement>(null);

  const scrollBy = (direction: number) => {
    if (!railRef.current) return;
    railRef.current.scrollBy({ left: direction * 320, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      <div className="mb-3 hidden items-center justify-end gap-1.5 md:flex">
        <button
          type="button"
          aria-label="向左滚动"
          onClick={() => scrollBy(-1)}
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
        >
          <i className="ri-arrow-left-line text-sm" />
        </button>
        <button
          type="button"
          aria-label="向右滚动"
          onClick={() => scrollBy(1)}
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
        >
          <i className="ri-arrow-right-line text-sm" />
        </button>
      </div>

      <div
        ref={railRef}
        className="no-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 md:gap-4"
      >
        {items.map((video) => (
          <Link
            key={video.id}
            to={`/detail/${video.id}`}
            className="group w-[240px] shrink-0 snap-start md:w-[280px]"
          >
            <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-primary-950">
              <img
                src={video.cover}
                alt={`${video.title} - 热门推荐封面`}
                title={`${video.title} - ${video.region}${video.category}视频`}
                className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-primary-950/5 to-transparent" />
              <span className="absolute right-2 top-2 rounded bg-primary-950/75 px-1.5 py-0.5 text-[10px] text-background-100">
                {video.duration}
              </span>
              <div className="absolute inset-x-0 bottom-0 p-2.5">
                <p className="line-clamp-1 text-[13px] font-medium text-background-50">{video.title}</p>
                <p className="mt-1 flex items-center gap-2 text-[10px] text-background-300">
                  <span>{video.year}</span>
                  <span className="h-2.5 w-px bg-background-50/30" />
                  <span className="flex items-center gap-0.5">
                    <span className="flex h-3 w-3 items-center justify-center">
                      <i className="ri-eye-line text-[10px]" />
                    </span>
                    {formatViews(video.views)}
                  </span>
                  <span className="ml-auto rounded bg-background-50/15 px-1.5 py-0.5 text-background-100">
                    {video.region}
                  </span>
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}